/**
 * `psr-meta-v2` — the additive, deterministic Public Sentiment projection the
 * redesigned report reads (types, key list, caps, reader). Dependency-free so
 * the persistence strip, the document builder and the depth gate can import it
 * without pulling in the sentiment pipeline.
 *
 * Contract:
 *  - Every field is OPTIONAL on `SiteRagReport.publicSentimentMeta`. A report
 *    without `projectionVersion === 'psr-meta-v2'` is a legacy report and
 *    renders exactly as before.
 *  - Built once at generation from the frozen SED section and the screening
 *    receipt; never mutated afterwards (the meta-instruction strip exempts it).
 *  - The canonical document, depth/dedup/audit section text and share
 *    projection see ONLY the legacy meta keys ({@link withoutPublicSentimentMetaV2}),
 *    so these fields never reach a public share, an office export's
 *    accountability inventory or a quality gate.
 *  - Persistence may turn `undefined` into `null` (client save paths). Readers
 *    go through {@link readPublicSentimentMetaV2}, which treats `null` as absent.
 */
import type { PublicSentimentBtmTech, PublicSentimentScope } from '../../types/siteEvidence';
import type { SentimentReasonCode } from './publicSentimentReasonCodes';

export const PSR_META_V2_VERSION = 'psr-meta-v2' as const;

/** Which deterministic product this report is. */
export type PsrReportState = 'assessed' | 'no-dated-local-stance' | 'coverage-only';
/**
 * Where the prose came from. `unavailable` = the model narrative failed and
 * the INSUFFICIENT_DATA fallback (or degraded skeleton) was stored; the
 * projection itself is still exact because it is built from the SED.
 */
export type PsrNarrativeState = 'generated' | 'deterministic' | 'unavailable';
export type PsrStance = -1 | 0 | 1;
export type PsrLedgerStatus = 'local-stance' | 'context-stance' | 'unscored';

export interface PsrScopeCount {
  scope: PublicSentimentScope;
  total: number;
  /** Rows without a stance shown in this report (sums with `unscoredByReason`). */
  unscored: number;
  /** Stance counts: assessed reports only. Local = decision-engine qualified rows. */
  supportive?: number;
  neutralStated?: number;
  opposed?: number;
}

/** Site scope, selected technologies, assessed reports only. */
export interface PsrTechStance {
  btmTech: PublicSentimentBtmTech;
  supportive: number;
  neutralStated: number;
  opposed: number;
  unscored: number;
  total: number;
}

export interface PsrTechSourceCount {
  btmTech: PublicSentimentBtmTech;
  sourceId: string;
  /** Rows at every scope. */
  rows: number;
  /** Rows at site scope. */
  siteRows: number;
  /** Qualified local stance rows (always 0 outside assessed reports). */
  assessedLocal: number;
}

export interface PsrUnscoredReason {
  reasonCode: SentimentReasonCode;
  local: number;
  context: number;
}

export interface PsrLedgerRow {
  /** 1-based position in the full SPEC order (stable when rows are capped). */
  n: number;
  scope: PublicSentimentScope;
  sourceId: string;
  btmTech: PublicSentimentBtmTech;
  status: PsrLedgerStatus;
  observedDate?: string;
  dateBasis?: 'published' | 'event' | 'scheduled-event' | 'first-seen';
  /** Dated after the scan (e.g. an agenda for a meeting not yet held). */
  afterScan?: true;
  /** Only for `local-stance` and (assessed reports) `context-stance`. */
  stance?: PsrStance;
  confidence?: number;
  /** Only for `unscored`. */
  reasonCode?: SentimentReasonCode;
  /** Original text of a pass-through / legacy reason. */
  reasonNote?: string;
  snippet: string;
  snippetTruncated?: true;
  url?: string;
  /** The row had no usable http(s) URL, or it exceeded the length cap. */
  urlOmitted?: true;
}

export interface PsrLedgerCounts { local: number; context: number }

export interface PsrSourceFailure {
  sourceId: string;
  error: string;
  errorTruncated?: true;
}

/**
 * Last prior scan. Assessed reports carry comparison numbers only when the
 * prior scan used the same query and its delta reconciles; other states carry
 * no numbers at all.
 */
export interface PsrPriorScan {
  fetchedAt: string;
  sameQuery: boolean;
  entryCount?: number;
  localObservationCount?: number;
  supportIndex?: number;
  supportIndexDelta?: number;
  addedObservationCount?: number;
}

export interface PsrArchiveAudit {
  jurisdiction: string;
  archiveUrl?: string;
  fetchedAt?: string;
  listedDocuments?: number;
  inWindowDocuments?: number;
  readDocuments?: number;
  relevantDocuments?: number;
  readPages?: number;
  ocrPages?: number;
  htmlDocuments?: number;
  listedAttachments?: number;
  readAttachments?: number;
  /**
   * In-window documents actually read: `readDocuments` minus
   * `readAttachments`. The collector counts every read, including linked
   * attachments, which are NOT windowed (municipalArchive.ts), so
   * `inWindowDocuments - readDocuments` is not the unread remainder;
   * `inWindowDocuments - readInWindowDocuments` is. Absent when negative.
   */
  readInWindowDocuments?: number;
  /**
   * listed ≥ in-window ≥ read-in-window, and all reads ≥ relevant, with every
   * count present. False ⇒ draw a table, never a funnel or a negative remainder.
   */
  funnelConsistent: boolean;
  errorCount: number;
  errors: string[];
}

export interface PsrTier1Fact {
  sourceId: string;
  key?: string;
  label: string;
  value: string | number;
  units?: string;
  url?: string;
  observedAt: string;
}

export interface PsrExclusionCounts {
  total: number;
  byReason: Array<{ reason: string; count: number }>;
}

export interface PublicSentimentMetaV2 {
  projectionVersion: typeof PSR_META_V2_VERSION;
  reportState: PsrReportState;
  narrative: PsrNarrativeState;
  /** Attempted stance (orchestrator) and context (tier-1) sources, in attempt order. */
  sourceRoles: { stanceSources: string[]; contextSources: string[] };
  sourceFailures?: PsrSourceFailure[];
  scopeCounts?: PsrScopeCount[];
  stanceByTech?: PsrTechStance[];
  countsByTechSource?: PsrTechSourceCount[];
  unscoredByReason?: PsrUnscoredReason[];
  ledger?: PsrLedgerRow[];
  ledgerTotal?: PsrLedgerCounts;
  ledgerOmitted?: PsrLedgerCounts;
  priorScan?: PsrPriorScan;
  municipalArchiveAudit?: PsrArchiveAudit[];
  tier1Facts?: PsrTier1Fact[];
  tier1FactsOmitted?: number;
  discoveryExclusionCounts?: PsrExclusionCounts;
}

/** Every v2 key. Anything else on `publicSentimentMeta` is legacy. */
export const PUBLIC_SENTIMENT_META_V2_KEYS = Object.freeze([
  'projectionVersion', 'reportState', 'narrative', 'sourceRoles', 'sourceFailures', 'scopeCounts',
  'stanceByTech', 'countsByTechSource', 'unscoredByReason', 'ledger', 'ledgerTotal', 'ledgerOmitted',
  'priorScan', 'municipalArchiveAudit', 'tier1Facts', 'tier1FactsOmitted', 'discoveryExclusionCounts',
] as const satisfies ReadonlyArray<keyof PublicSentimentMetaV2>);

/** Caps (the builder enforces them; tests prove them). */
export const PSR_META_V2_LIMITS = Object.freeze({
  ledgerLocalRows: 240,
  ledgerContextRows: 60,
  snippetCodePoints: 240,
  errorCodePoints: 300,
  urlChars: 2048,
  archiveErrors: 5,
  archives: 5,
  tier1Facts: 40,
  tier1TextCodePoints: 160,
  exclusionReasons: 20,
  /** Serialized UTF-8 bytes of the whole v2 object. */
  maxBytes: 160_000,
});

const V2_KEY_SET: ReadonlySet<string> = new Set(PUBLIC_SENTIMENT_META_V2_KEYS);

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** True for a meta object stamped with the v2 projection. */
export function isPublicSentimentMetaV2(meta: unknown): boolean {
  return isPlainRecord(meta) && meta.projectionVersion === PSR_META_V2_VERSION;
}

/**
 * The legacy view of `publicSentimentMeta`: v2 keys removed, remaining keys in
 * their original order. Returns the input unchanged (same reference) when it
 * is not a v2 meta, so legacy reports are untouched byte-for-byte.
 */
export function withoutPublicSentimentMetaV2<T>(meta: T): T {
  if (!isPublicSentimentMetaV2(meta)) return meta;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(meta as Record<string, unknown>)) {
    if (!V2_KEY_SET.has(key)) out[key] = value;
  }
  return out as T;
}

/* ── Null-tolerant reader ─────────────────────────────────────────────── */

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const count = (value: unknown): value is number => finite(value) && value >= 0 && Number.isInteger(value);
const text = (value: unknown): value is string => typeof value === 'string';
const SCOPES: ReadonlySet<string> = new Set(['site', 'state', 'country']);
const STATUSES: ReadonlySet<string> = new Set(['local-stance', 'context-stance', 'unscored']);
const STATES: ReadonlySet<string> = new Set(['assessed', 'no-dated-local-stance', 'coverage-only']);
const NARRATIVES: ReadonlySet<string> = new Set(['generated', 'deterministic', 'unavailable']);

function pick<T extends Record<string, unknown>>(source: Record<string, unknown>, rules: { [K in keyof T]?: (value: unknown) => boolean }): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, test] of Object.entries(rules) as Array<[string, (value: unknown) => boolean]>) {
    const value = source[key];
    // `null` (a JSON round trip of `undefined`) and invalid values are absent.
    if (value !== null && value !== undefined && test(value)) out[key] = value;
  }
  return out as Partial<T>;
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isPlainRecord) : [];
}

const stance = (value: unknown) => value === -1 || value === 0 || value === 1;
const strings = (value: unknown) => Array.isArray(value) && value.every(text);

/**
 * Read a persisted v2 projection. `undefined` for legacy / malformed meta.
 * `null` fields are treated as absent (never as 0, "", or a neutral stance),
 * and rows missing a required field are dropped rather than defaulted.
 */
export function readPublicSentimentMetaV2(meta: unknown): PublicSentimentMetaV2 | undefined {
  if (!isPublicSentimentMetaV2(meta)) return undefined;
  const raw = meta as Record<string, unknown>;
  if (!text(raw.reportState) || !STATES.has(raw.reportState) || !text(raw.narrative) || !NARRATIVES.has(raw.narrative)) return undefined;
  const roles = isPlainRecord(raw.sourceRoles) ? raw.sourceRoles : {};
  const out: PublicSentimentMetaV2 = {
    projectionVersion: PSR_META_V2_VERSION,
    reportState: raw.reportState as PsrReportState,
    narrative: raw.narrative as PsrNarrativeState,
    sourceRoles: {
      stanceSources: strings(roles.stanceSources) ? [...roles.stanceSources as string[]] : [],
      contextSources: strings(roles.contextSources) ? [...roles.contextSources as string[]] : [],
    },
  };
  const assessed = out.reportState === 'assessed';
  if (Array.isArray(raw.sourceFailures)) {
    out.sourceFailures = records(raw.sourceFailures).filter(row => text(row.sourceId) && text(row.error))
      .map(row => pick<PsrSourceFailure & Record<string, unknown>>(row, { sourceId: text, error: text, errorTruncated: v => v === true }) as PsrSourceFailure);
  }
  if (out.reportState === 'coverage-only') return out;
  if (Array.isArray(raw.scopeCounts)) {
    out.scopeCounts = records(raw.scopeCounts).filter(row => SCOPES.has(row.scope as string) && count(row.total) && count(row.unscored))
      .map(row => pick<PsrScopeCount & Record<string, unknown>>(row, {
        scope: text, total: count, unscored: count,
        ...(assessed ? { supportive: count, neutralStated: count, opposed: count } : {}),
      }) as PsrScopeCount);
  }
  if (assessed && Array.isArray(raw.stanceByTech)) {
    out.stanceByTech = records(raw.stanceByTech)
      .filter(row => text(row.btmTech) && ['supportive', 'neutralStated', 'opposed', 'unscored', 'total'].every(key => count(row[key])))
      .map(row => pick<PsrTechStance & Record<string, unknown>>(row, { btmTech: text, supportive: count, neutralStated: count, opposed: count, unscored: count, total: count }) as PsrTechStance);
  }
  if (Array.isArray(raw.countsByTechSource)) {
    out.countsByTechSource = records(raw.countsByTechSource)
      .filter(row => text(row.btmTech) && text(row.sourceId) && count(row.rows) && count(row.siteRows) && count(row.assessedLocal))
      .map(row => pick<PsrTechSourceCount & Record<string, unknown>>(row, { btmTech: text, sourceId: text, rows: count, siteRows: count, assessedLocal: count }) as PsrTechSourceCount);
  }
  if (Array.isArray(raw.unscoredByReason)) {
    out.unscoredByReason = records(raw.unscoredByReason).filter(row => text(row.reasonCode) && count(row.local) && count(row.context))
      .map(row => pick<PsrUnscoredReason & Record<string, unknown>>(row, { reasonCode: text, local: count, context: count }) as PsrUnscoredReason);
  }
  if (Array.isArray(raw.ledger)) {
    out.ledger = records(raw.ledger)
      .filter(row => count(row.n) && SCOPES.has(row.scope as string) && text(row.sourceId) && text(row.btmTech)
        && text(row.status) && STATUSES.has(row.status) && text(row.snippet)
        && (assessed || row.status === 'unscored'))
      .map(row => {
        const stanceRow = row.status !== 'unscored';
        return pick<PsrLedgerRow & Record<string, unknown>>(row, {
          n: count, scope: text, sourceId: text, btmTech: text, status: text,
          observedDate: text, dateBasis: text, afterScan: v => v === true,
          ...(stanceRow ? { stance, confidence: v => finite(v) && v > 0 && v <= 1 } : { reasonCode: text, reasonNote: text }),
          snippet: text, snippetTruncated: v => v === true, url: text, urlOmitted: v => v === true,
        }) as PsrLedgerRow;
      })
      // A stance row whose stance or confidence did not survive is not a stance row.
      .filter(row => row.status === 'unscored' || (row.stance !== undefined && row.confidence !== undefined));
  }
  for (const key of ['ledgerTotal', 'ledgerOmitted'] as const) {
    const value = raw[key];
    if (isPlainRecord(value) && count(value.local) && count(value.context)) out[key] = { local: value.local, context: value.context };
  }
  if (isPlainRecord(raw.priorScan) && text(raw.priorScan.fetchedAt) && typeof raw.priorScan.sameQuery === 'boolean') {
    out.priorScan = pick<PsrPriorScan & Record<string, unknown>>(raw.priorScan, {
      fetchedAt: text, sameQuery: v => typeof v === 'boolean',
      ...(assessed ? { entryCount: count, localObservationCount: count, supportIndex: finite, supportIndexDelta: finite, addedObservationCount: count } : {}),
    }) as PsrPriorScan;
  }
  if (Array.isArray(raw.municipalArchiveAudit)) {
    out.municipalArchiveAudit = records(raw.municipalArchiveAudit).filter(row => text(row.jurisdiction) && typeof row.funnelConsistent === 'boolean' && count(row.errorCount))
      .map(row => ({
        ...pick<PsrArchiveAudit & Record<string, unknown>>(row, {
          jurisdiction: text, archiveUrl: text, fetchedAt: text, listedDocuments: count, inWindowDocuments: count, readDocuments: count,
          relevantDocuments: count, readPages: count, ocrPages: count, htmlDocuments: count, listedAttachments: count, readAttachments: count,
          readInWindowDocuments: count,
          funnelConsistent: v => typeof v === 'boolean', errorCount: count,
        }),
        errors: strings(row.errors) ? [...row.errors as string[]] : [],
      }) as PsrArchiveAudit);
  }
  if (Array.isArray(raw.tier1Facts)) {
    out.tier1Facts = records(raw.tier1Facts).filter(row => text(row.sourceId) && text(row.label) && (text(row.value) || finite(row.value)) && text(row.observedAt))
      .map(row => pick<PsrTier1Fact & Record<string, unknown>>(row, { sourceId: text, key: text, label: text, value: v => text(v) || finite(v), units: text, url: text, observedAt: text }) as PsrTier1Fact);
  }
  if (count(raw.tier1FactsOmitted)) out.tier1FactsOmitted = raw.tier1FactsOmitted;
  if (isPlainRecord(raw.discoveryExclusionCounts) && count(raw.discoveryExclusionCounts.total)) {
    out.discoveryExclusionCounts = {
      total: raw.discoveryExclusionCounts.total,
      byReason: records(raw.discoveryExclusionCounts.byReason).filter(row => text(row.reason) && count(row.count))
        .map(row => ({ reason: row.reason as string, count: row.count as number })),
    };
  }
  return out;
}

/* ── Coverage display state ───────────────────────────────────────────── */

/** Glyph states (SPEC §1): ● full, ◐ partial, ○ none, ✕ failed, ◌ unreported. */
export type PsrCoverageState = 'full' | 'partial' | 'none' | 'failed' | 'unreported';

export interface PsrSourceCoverageState {
  sourceId: string;
  /** `requested` = a coverage row for a source that was never attempted (e.g. tribal records). */
  role: 'stance' | 'context' | 'requested';
  coverage: PsrCoverageState;
  /** Every coverage row for this source, in receipt order. A source can have several. */
  rows: Array<{ coverage: string; errors: string[] }>;
  /** All row errors, de-duplicated, in order. */
  errors: string[];
  /** Verbatim `section.reason` failure; set only when the source has NO coverage row. */
  failure?: string;
}

type CoverageReceipt = { sourceCoverage?: Array<{ sourceId: string; coverage: string; errors: string[] }> | null };
const COVERAGE_RANK: Record<string, number> = { none: 0, partial: 1, full: 2 };

/**
 * One display row per source for the coverage matrix, from the FROZEN scan
 * receipt (`report.screeningBasis.sentimentScan`) plus the v2 source roles
 * and failures:
 *  - several coverage rows for one source ⇒ the WORST row's state (never the
 *    first), with every row and error kept;
 *  - ✕ failed only for an attempted source with no coverage row and a
 *    recorded failure — a failure never overrides a coverage row;
 *  - ◌ unreported for an attempted source with neither;
 *  - coverage rows for sources that were never attempted are still shown.
 */
export function publicSentimentSourceCoverageStates(
  scan: CoverageReceipt | null | undefined,
  meta: Pick<PublicSentimentMetaV2, 'sourceRoles' | 'sourceFailures'> | null | undefined,
): PsrSourceCoverageState[] {
  if (!meta) return [];
  const coverage = Array.isArray(scan?.sourceCoverage) ? scan!.sourceCoverage.filter(row => isPlainRecord(row) && text(row.sourceId)) : [];
  const attempted: Array<{ sourceId: string; role: 'stance' | 'context' }> = [
    ...meta.sourceRoles.stanceSources.map(sourceId => ({ sourceId, role: 'stance' as const })),
    ...meta.sourceRoles.contextSources.filter(id => !meta.sourceRoles.stanceSources.includes(id)).map(sourceId => ({ sourceId, role: 'context' as const })),
  ];
  const requested = [...new Set(coverage.map(row => row.sourceId))]
    .filter(id => !attempted.some(row => row.sourceId === id))
    .map(sourceId => ({ sourceId, role: 'requested' as const }));
  return [...attempted, ...requested].map(({ sourceId, role }) => {
    const rows = coverage.filter(row => row.sourceId === sourceId)
      .map(row => ({ coverage: row.coverage, errors: Array.isArray(row.errors) ? row.errors.filter(text) : [] }));
    const errors = [...new Set(rows.flatMap(row => row.errors))];
    if (rows.length) {
      const worst = rows.reduce((min, row) => Math.min(min, COVERAGE_RANK[row.coverage] ?? 0), 2);
      const state = (['none', 'partial', 'full'] as const)[worst]!;
      return { sourceId, role, coverage: state, rows, errors };
    }
    const failure = (meta.sourceFailures ?? []).find(row => row.sourceId === sourceId)?.error;
    return failure
      ? { sourceId, role, coverage: 'failed' as const, rows, errors, failure }
      : { sourceId, role, coverage: 'unreported' as const, rows, errors };
  });
}
